import { BOARD_SIZE } from "./constants";
import { colorWeights } from "./levelConfig";
import { cloneBoard, inBounds, rotatePosition } from "./grid";
import { Rng } from "./rng";
import type {
  BoardSize,
  BoardState,
  Cell,
  GravityDir,
  LevelConfig,
  Position,
  RotationDir,
  SpecialKind,
  TileData
} from "./types";

function emptyCell(): Cell {
  return { kind: "EMPTY", tile: null };
}

function tileCell(color: number, special: SpecialKind = "NONE", lockHp: 0 | 1 = 0): Cell {
  return {
    kind: "TILE",
    tile: {
      color,
      special,
      lockHp
    }
  };
}

const COLOR_HUNT_REFILL_WEIGHT_MULTIPLIER = 1.25;

function sampleColorFromWeights(rng: Rng, weights: readonly number[]): number {
  const totalWeight = weights.reduce((acc, n) => acc + n, 0);
  const pick = rng.nextFloat() * totalWeight;
  let running = 0;
  for (let i = 0; i < weights.length; i += 1) {
    running += weights[i];
    if (pick <= running) {
      return i;
    }
  }
  return Math.max(0, weights.length - 1);
}

function sampleColor(rng: Rng, tileTypes: number): number {
  return sampleColorFromWeights(rng, colorWeights(tileTypes));
}

function normalizeRefillTargetColors(tileTypes: number, targetColors?: readonly number[]): number[] {
  if (targetColors == null || targetColors.length === 0) {
    return [];
  }
  return [...new Set(targetColors.filter(
    (color) => Number.isInteger(color) && color >= 0 && color < Math.max(0, tileTypes)
  ))];
}

export function refillColorWeights(tileTypes: number, targetColors?: readonly number[]): number[] {
  const weights = colorWeights(tileTypes);
  const refillTargets = normalizeRefillTargetColors(tileTypes, targetColors);
  for (const color of refillTargets) {
    weights[color] *= COLOR_HUNT_REFILL_WEIGHT_MULTIPLIER;
  }
  return weights;
}

export function makeRandomTile(level: LevelConfig, rng: Rng, lockHp: 0 | 1 = 0): TileData {
  return {
    color: sampleColor(rng, level.tileTypes),
    special: "NONE",
    lockHp
  };
}

export function makeRefillTile(
  level: LevelConfig,
  rng: Rng,
  targetColors?: readonly number[],
  lockHp: 0 | 1 = 0
): TileData {
  return {
    color: sampleColorFromWeights(rng, refillColorWeights(level.tileTypes, targetColors)),
    special: "NONE",
    lockHp
  };
}

export function createEmptyBoard(gravity: GravityDir = "DOWN", size: BoardSize = BOARD_SIZE as BoardSize): BoardState {
  const cells: Cell[][] = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => emptyCell())
  );
  return {
    size,
    cells,
    gravity
  };
}

function causesImmediateLineMatch(cells: Cell[][], row: number, col: number, color: number): boolean {
  if (col >= 2) {
    const a = cells[row][col - 1];
    const b = cells[row][col - 2];
    if (a.kind === "TILE" && b.kind === "TILE" && a.tile != null && b.tile != null) {
      if (a.tile.color === color && b.tile.color === color) {
        return true;
      }
    }
  }
  if (row >= 2) {
    const a = cells[row - 1][col];
    const b = cells[row - 2][col];
    if (a.kind === "TILE" && b.kind === "TILE" && a.tile != null && b.tile != null) {
      if (a.tile.color === color && b.tile.color === color) {
        return true;
      }
    }
  }
  return false;
}

function applyObstaclePlacement(board: BoardState, level: LevelConfig, rng: Rng): void {
  const size = board.size;
  const all: Position[] = [];
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      all.push({ row, col });
    }
  }
  rng.shuffleInPlace(all);

  const requestedCrates = Math.floor(size * size * level.cratePct);
  const requestedSteel = Math.floor(size * size * level.steelPct);
  const staticCap = Math.min(18, size * size - 65);
  const totalStaticRequested = requestedCrates + requestedSteel;
  const staticScale =
    totalStaticRequested > staticCap && totalStaticRequested > 0
      ? staticCap / totalStaticRequested
      : 1;
  const crateCount = Math.floor(requestedCrates * staticScale);
  const steelCount = Math.floor(requestedSteel * staticScale);

  let cursor = 0;
  for (let i = 0; i < crateCount && cursor < all.length; i += 1, cursor += 1) {
    const { row, col } = all[cursor];
    board.cells[row][col] = { kind: "CRATE", tile: null };
  }
  for (let i = 0; i < steelCount && cursor < all.length; i += 1, cursor += 1) {
    const { row, col } = all[cursor];
    board.cells[row][col] = { kind: "STEEL", tile: null };
  }

  const requestedLocks = Math.floor(size * size * level.lockPct);
  const lockCandidates: Position[] = [];
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      const cell = board.cells[row][col];
      if (cell.kind === "TILE" && cell.tile != null) {
        lockCandidates.push({ row, col });
      }
    }
  }
  rng.shuffleInPlace(lockCandidates);
  const lockCount = Math.min(requestedLocks, lockCandidates.length);
  for (let i = 0; i < lockCount; i += 1) {
    const { row, col } = lockCandidates[i];
    const cell = board.cells[row][col];
    if (cell.tile != null) {
      cell.tile.lockHp = 1;
    }
  }
}

export function injectObstaclesForLevel(board: BoardState, level: LevelConfig, rng: Rng): void {
  const size = board.size;
  const requestedCrates = Math.floor(size * size * level.cratePct);
  const requestedSteel = Math.floor(size * size * level.steelPct);
  const staticCap = Math.min(18, size * size - 65);
  const totalStaticRequested = requestedCrates + requestedSteel;
  const staticScale =
    totalStaticRequested > staticCap && totalStaticRequested > 0
      ? staticCap / totalStaticRequested
      : 1;
  const targetCrates = Math.floor(requestedCrates * staticScale);
  const targetSteel = Math.floor(requestedSteel * staticScale);

  let currentCrates = 0;
  let currentSteel = 0;
  const tileCandidates: Position[] = [];
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      const cell = board.cells[row][col];
      if (cell.kind === "CRATE") currentCrates += 1;
      if (cell.kind === "STEEL") currentSteel += 1;
      if (cell.kind === "TILE" && cell.tile != null) {
        tileCandidates.push({ row, col });
      }
    }
  }

  rng.shuffleInPlace(tileCandidates);
  let cursor = 0;
  while (currentCrates < targetCrates && cursor < tileCandidates.length) {
    const { row, col } = tileCandidates[cursor];
    cursor += 1;
    const cell = board.cells[row][col];
    if (cell.kind !== "TILE") continue;
    board.cells[row][col] = { kind: "CRATE", tile: null };
    currentCrates += 1;
  }

  while (currentSteel < targetSteel && cursor < tileCandidates.length) {
    const { row, col } = tileCandidates[cursor];
    cursor += 1;
    const cell = board.cells[row][col];
    if (cell.kind !== "TILE") continue;
    board.cells[row][col] = { kind: "STEEL", tile: null };
    currentSteel += 1;
  }

  const targetLocks = Math.floor(size * size * level.lockPct);
  let currentLocks = 0;
  const unlockCandidates: Position[] = [];
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      const cell = board.cells[row][col];
      if (cell.kind !== "TILE" || cell.tile == null) continue;
      if (cell.tile.lockHp > 0) {
        currentLocks += 1;
      } else {
        unlockCandidates.push({ row, col });
      }
    }
  }

  rng.shuffleInPlace(unlockCandidates);
  for (let i = 0; i < unlockCandidates.length && currentLocks < targetLocks; i += 1) {
    const { row, col } = unlockCandidates[i];
    const cell = board.cells[row][col];
    if (cell.kind === "TILE" && cell.tile != null && cell.tile.lockHp === 0) {
      cell.tile.lockHp = 1;
      currentLocks += 1;
    }
  }
}

export function createBoardForLevel(level: LevelConfig, rng: Rng, size: BoardSize = BOARD_SIZE as BoardSize): BoardState {
  const board = createEmptyBoard("DOWN", size);
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      let attempts = 0;
      let color = sampleColor(rng, level.tileTypes);
      while (attempts < 20 && causesImmediateLineMatch(board.cells, row, col, color)) {
        color = sampleColor(rng, level.tileTypes);
        attempts += 1;
      }
      board.cells[row][col] = tileCell(color);
    }
  }
  applyObstaclePlacement(board, level, rng);
  return board;
}

export function isMovableCell(cell: Cell): boolean {
  return cell.kind === "TILE" && cell.tile != null;
}

export function isSwappableCell(cell: Cell): boolean {
  return cell.kind === "TILE" && cell.tile != null && cell.tile.lockHp === 0;
}

export function isStaticObstacle(cell: Cell): boolean {
  return cell.kind === "CRATE" || cell.kind === "STEEL";
}

export function getCell(board: BoardState, pos: Position): Cell | null {
  if (!inBounds(board.size, pos.row, pos.col)) {
    return null;
  }
  return board.cells[pos.row][pos.col];
}

export function canSwap(board: BoardState, a: Position, b: Position): boolean {
  if (!inBounds(board.size, a.row, a.col) || !inBounds(board.size, b.row, b.col)) {
    return false;
  }
  const manhattan = Math.abs(a.row - b.row) + Math.abs(a.col - b.col);
  if (manhattan !== 1) {
    return false;
  }
  return isSwappableCell(board.cells[a.row][a.col]) && isSwappableCell(board.cells[b.row][b.col]);
}

export function swapCells(board: BoardState, a: Position, b: Position): void {
  const tmp = board.cells[a.row][a.col];
  board.cells[a.row][a.col] = board.cells[b.row][b.col];
  board.cells[b.row][b.col] = tmp;
}

export function rotateBoard(board: BoardState, dir: RotationDir): void {
  const next = cloneBoard(board);
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      const rotated = rotatePosition(board.size, { row, col }, dir);
      next.cells[rotated.row][rotated.col] = board.cells[row][col];
    }
  }
  board.cells = next.cells;
  board.gravity = "DOWN";
}

export function serializeBoard(board: BoardState): BoardState {
  return cloneBoard(board);
}

export function deserializeBoard(serialized: BoardState): BoardState {
  return cloneBoard(serialized);
}
